/**
 * Demo display names (owner, 2026-09-25): the app shows Indian index and stock
 * names in place of its 20 market tickers, "USDT" as "INR" and "USD-M Futures"
 * as "Futures". NAMES AND LABELS ONLY: prices, candles, amounts, orders, the
 * database and every request the server receives are unchanged.
 *
 * - Answers (API and WebSocket) rename only the 20 full tickers (`toDemo`), so
 *   the rename reverses exactly; requests rename them back (`fromDemo`), never
 *   touching /auth/* or typed text such as passwords, reasons and comments.
 * - Base assets, coin names, "USDT" and "USD-M" change on SCREEN only
 *   (`toScreenText`), never in data.
 * - The browser stores the REAL tickers (`toReal`), so turning the demo off
 *   leaves nothing renamed behind.
 * - Fails closed: renaming starts only after the server has answered that it is
 *   NOT live, and a LIVE answer switches it off.
 *
 * To revert: set DEMO_NAMES_ENABLED to false (or revert the commit that added
 * this file) and redeploy.
 */

export const DEMO_NAMES_ENABLED = false;

/** The server's 20 markets (backend/app/data/universe.py FUTURES_UNIVERSE):
 *  [ticker, demo ticker, base asset, coin name, demo name]. Demo tickers are
 *  five letters or more, like every real ticker, so symbol checks still pass. */
export const DEMO_TABLE: ReadonlyArray<readonly [string, string, string, string, string]> = [
  ['BTCUSDT', 'SENSEX', 'BTC', 'Bitcoin', 'BSE Sensex'],
  ['ETHUSDT', 'NIFTY50', 'ETH', 'Ethereum', 'Nifty 50'],
  ['BNBUSDT', 'BANKNIFTY', 'BNB', 'BNB', 'Nifty Bank'],
  ['XRPUSDT', 'RELIANCE', 'XRP', 'XRP', 'Reliance Industries'],
  ['SOLUSDT', 'HDFCBANK', 'SOL', 'Solana', 'HDFC Bank'],
  ['TRXUSDT', 'BHARTIARTL', 'TRX', 'TRON', 'Bharti Airtel'],
  ['ZECUSDT', 'ICICIBANK', 'ZEC', 'Zcash', 'ICICI Bank'],
  ['ADAUSDT', 'HINDUNILVR', 'ADA', 'Cardano', 'Hindustan Unilever'],
  ['LINKUSDT', 'BAJFINANCE', 'LINK', 'Chainlink', 'Bajaj Finance'],
  ['XLMUSDT', 'KOTAKBANK', 'XLM', 'Stellar', 'Kotak Mahindra Bank'],
  ['BCHUSDT', 'AXISBANK', 'BCH', 'Bitcoin Cash', 'Axis Bank'],
  ['LTCUSDT', 'MARUTI', 'LTC', 'Litecoin', 'Maruti Suzuki'],
  ['HBARUSDT', 'SUNPHARMA', 'HBAR', 'Hedera', 'Sun Pharma'],
  ['SUIUSDT', 'TITAN', 'SUI', 'Sui', 'Titan Company'],
  ['AVAXUSDT', 'ASIANPAINT', 'AVAX', 'Avalanche', 'Asian Paints'],
  ['NEARUSDT', 'WIPRO', 'NEAR', 'NEAR Protocol', 'Wipro'],
  ['UNIUSDT', 'HCLTECH', 'UNI', 'Uniswap', 'HCL Technologies'],
  ['TAOUSDT', 'TATASTEEL', 'TAO', 'Bittensor', 'Tata Steel'],
  ['AAVEUSDT', 'ULTRACEMCO', 'AAVE', 'Aave', 'UltraTech Cement'],
  ['DOTUSDT', 'POWERGRID', 'DOT', 'Polkadot', 'Power Grid'],
];

/** Full ticker -> demo ticker (data coming IN). */
const IN = new Map<string, string>();
/** Demo ticker -> full ticker (requests going OUT, and what the browser stores). */
const OUT = new Map<string, string>();
/** Everything a viewer may read -> its demo text (screen only). */
const SCREEN = new Map<string, string>([['USDT', 'INR']]);
/** Demo ticker -> demo company name, for search. */
const DEMO_NAME = new Map<string, string>();
for (const [symbol, demo, base, name, demoName] of DEMO_TABLE) {
  IN.set(symbol, demo);
  OUT.set(demo, symbol);
  OUT.set(`${demo}USDT`, symbol); // code that rebuilds a pair from its base (`${base}USDT`)
  SCREEN.set(symbol, demo);
  SCREEN.set(base, demo);
  if (name !== base) SCREEN.set(name, demoName);
  DEMO_NAME.set(demo, demoName);
}

const escape = (text: string): string => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
// Whole tokens only (BTC never matches inside BTCUSDT) and case-sensitive.
const tokens = (table: Map<string, string>): RegExp =>
  new RegExp(
    `(?<![A-Za-z0-9])(${[...table.keys()].sort((a, b) => b.length - a.length).map(escape).join('|')})(?![A-Za-z0-9])`,
    'g',
  );
const IN_RE = tokens(IN);
const OUT_RE = tokens(OUT);
const SCREEN_RE = tokens(SCREEN);

// ── Fail-closed activation ──────────────────────────────────────────────────
// Renaming runs only in a page that loaded after the server said "not LIVE"
// (a flag in localStorage). No flag, blocked storage, or a LIVE answer: off.
const OK_FLAG = 'bheema:demo-names:ok';

function readFlag(): boolean {
  try {
    return window.localStorage.getItem(OK_FLAG) === '1';
  } catch {
    return false;
  }
}

let active = DEMO_NAMES_ENABLED && readFlag();

export function demoNamesActive(): boolean {
  return active;
}

/** Unit tests only (node has no window, so the page flag cannot be set). */
export function setDemoNamesActiveForTests(value: boolean): void {
  active = DEMO_NAMES_ENABLED && value;
}

/**
 * Called with every /system/environment answer. Not live and not yet renaming:
 * record it and reload once into the renamed page. LIVE: clear it and, if this
 * page was renaming, stop at once and reload only when the clear stuck (so a
 * blocked storage can never loop).
 */
export function noteEnvironment(isLive: boolean): void {
  if (!DEMO_NAMES_ENABLED) return;
  try {
    if (isLive) {
      window.localStorage.removeItem(OK_FLAG);
      if (active) {
        active = false;
        if (!readFlag()) window.location.reload();
      }
    } else if (!active) {
      window.localStorage.setItem(OK_FLAG, '1');
      if (readFlag()) window.location.reload();
    }
  } catch {
    if (isLive) active = false;
  }
}

/** Asked once before the first render. Returns true when the page is reloading
 *  into demo mode (first visit after a "not live" answer): the defaults computed
 *  at load must see the names from the start, so the caller does not render.
 *  A failed or unauthorised read changes nothing. */
export async function primeDemoNames(): Promise<boolean> {
  if (!DEMO_NAMES_ENABLED) return false;
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), 3000);
  try {
    const response = await fetch('/api/system/environment', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      signal: controller.signal,
    });
    if (!response.ok) return false;
    const body = (await response.json()) as { is_live?: unknown };
    if (body.is_live === true) {
      window.localStorage.removeItem(OK_FLAG);
      active = false;
      return false;
    }
    if (body.is_live === false && !active) {
      window.localStorage.setItem(OK_FLAG, '1');
      if (readFlag()) {
        window.location.reload();
        return true;
      }
    }
    return false;
  } catch {
    // unreachable or blocked: keep the current state
    return false;
  } finally {
    window.clearTimeout(timer);
  }
}

// ── Mapping ─────────────────────────────────────────────────────────────────

function mapText(text: string, re: RegExp, table: Map<string, string>): string {
  if (text.length === 0) return text;
  re.lastIndex = 0;
  if (!re.test(text)) return text;
  re.lastIndex = 0;
  return text.replace(re, (token) => table.get(token) ?? token);
}

/** Keys whose text is typed or secret: never rewritten on the way out. */
const TYPED_KEY = /pass|secret|token|totp|otp|code|reason|comment|note|email|recovery|description/i;

function mapDeep(value: unknown, re: RegExp, table: Map<string, string>, skipTyped: boolean): unknown {
  if (typeof value === 'string') {
    // Numbers (prices, sizes, times) are the bulk of every payload: skip them fast.
    const first = value.charCodeAt(0);
    if ((first >= 48 && first <= 57) || first === 45) return value;
    return mapText(value, re, table);
  }
  if (Array.isArray(value)) {
    let out: unknown[] | null = null;
    for (let i = 0; i < value.length; i++) {
      const item: unknown = value[i];
      if (typeof item === 'number') continue;
      const mapped = mapDeep(item, re, table, skipTyped);
      if (mapped !== item) {
        out ??= value.slice();
        out[i] = mapped;
      }
    }
    return out ?? value;
  }
  if (value !== null && typeof value === 'object') {
    const source = value as Record<string, unknown>;
    let changed = false;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      const item = source[key];
      if (skipTyped && TYPED_KEY.test(key)) {
        out[key] = item;
        continue;
      }
      const newKey = mapText(key, re, table);
      const mapped = mapDeep(item, re, table, skipTyped);
      if (newKey !== key || mapped !== item) changed = true;
      out[newKey] = mapped;
    }
    return changed ? out : value;
  }
  return value;
}

/** Answers: the 20 full tickers -> demo tickers (exactly reversible: typed and
 *  secret fields are left alone both ways and read right on screen instead). */
export function toDemo<T>(value: T): T {
  return active ? (mapDeep(value, IN_RE, IN, true) as T) : value;
}

/** What the browser stores and sends: demo tickers -> real tickers, whenever
 *  the demo code is present (even when not renaming), so nothing renamed is
 *  kept or sent. Typed and secret fields are left exactly as typed. */
export function toReal<T>(value: T): T {
  return DEMO_NAMES_ENABLED ? (mapDeep(value, OUT_RE, OUT, true) as T) : value;
}

/** Requests: demo tickers -> real tickers; /auth/* untouched. */
export function fromDemo<T>(value: T, path = ''): T {
  if (path.startsWith('/auth')) return value;
  return toReal(value);
}

/** A single ticker for display, e.g. a built-in default. */
export function demoSymbol(symbol: string): string {
  return active ? (IN.get(symbol) ?? symbol) : symbol;
}

/** Symbol search: the server matches `q` against real tickers, so while the
 *  demo is on the browser filters by demo ticker and demo company name. */
export function matchesDemoSearch(demoTicker: string, typed: string): boolean {
  const q = typed.trim().toLowerCase();
  if (q.length === 0) return true;
  const name = DEMO_NAME.get(demoTicker) ?? '';
  return demoTicker.toLowerCase().includes(q) || name.toLowerCase().includes(q);
}

// ── Screen text ─────────────────────────────────────────────────────────────

// "USD-M Futures" / "USDⓈ-M perpetual futures" read as plain futures (owner).
const USD_M_RE = /USDⓈ?-M\s*/g;

/** On-screen text: demo tickers and names, "USDT" as "INR" (a LABEL: amounts
 *  are not converted), "USD-M" removed. Data is never changed here. */
export function toScreenText(text: string): string {
  if (!active) return text;
  const plain = text.includes('-M') ? text.replace(USD_M_RE, '') : text;
  return mapText(plain, SCREEN_RE, SCREEN);
}

const SKIP = new Set(['SCRIPT', 'STYLE', 'TEXTAREA', 'INPUT', 'CODE', 'PRE']);
const ATTRIBUTES = ['title', 'placeholder', 'aria-label'];

function renameTextNode(node: Text): void {
  const parent = node.parentElement;
  if (parent === null || SKIP.has(parent.tagName) || parent.isContentEditable) return;
  const before = node.nodeValue ?? '';
  const after = toScreenText(before);
  if (after !== before) node.nodeValue = after;
}

function renameAttributes(element: Element): void {
  for (const name of ATTRIBUTES) {
    const before = element.getAttribute(name);
    if (before === null) continue;
    const after = toScreenText(before);
    if (after !== before) element.setAttribute(name, after);
  }
}

function renameTree(root: Node): void {
  if (root.nodeType === Node.TEXT_NODE) {
    renameTextNode(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const element = root as Element;
  if (SKIP.has(element.tagName)) return;
  renameAttributes(element);
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let node = walker.nextNode(); node !== null; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) renameTextNode(node as Text);
    else renameAttributes(node as Element);
  }
}

/** Keeps every visible text node and label on demo text (idempotent). */
export function startDemoScreenText(): void {
  if (!active || typeof MutationObserver === 'undefined') return;
  renameTree(document.body);
  new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'characterData') renameTextNode(record.target as Text);
      else if (record.type === 'attributes') renameAttributes(record.target as Element);
      else record.addedNodes.forEach(renameTree);
    }
  }).observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: ATTRIBUTES,
  });
}
